import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { setLoaderProgress } from 'src/slices/game.slice';
import { SDKGameLoadingFinished } from 'src/utils/sdkDefinitions';
import { formatBytes, getExpectedOrigin, getLoaderAggregateProgressPercent } from 'src/utils/loader';
import ThemedLogo from './ThemedLogo';

const INITIAL_PROGRESS = {
    loadedBytes: 0,
    totalBytes: 0,
    progress: 0,
    filesStarted: 0,
    filesFinished: 0,
    filesPending: null,
    approximate: false,
};

// Wait 1 second after PokiSDK.gameLoadingFinished().
// Games usually take a little while to actually show the main menu after this.
const SDK_FINISHED_HIDE_DELAY_MS = 1000;

// If the game doesn't call PokiSDK.gameLoadingFinished())e, we fall back to hiding the overlay 2 seconds after all files are finished.
// Sometimes after some files have finished downloading, new ones will start.
const FILES_FINISHED_HIDE_DELAY_MS = 2000;

const LoaderProgressOverlayWrapper = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: var(--surface);
    color: var(--text-primary);
    pointer-events: none;
`;

const LoaderProgressContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
    width: min(640px, 100%);
    padding: 0 24px;

    @media (height <= 480px) {
        gap: 12px;
    }
`;

const LoaderProgressLogo = styled(ThemedLogo)`
    width: min(240px, 60%);
    margin: 0 0 32px;

    @media (height <= 480px) {
        margin: 0;
    }
`;

const LoaderProgressHeader = styled.div`
    display: flex;
    align-items: baseline;
    justify-content: center;
    gap: 12px;
    text-align: center;
`;

const LoaderProgressTitle = styled.strong`
    font-size: 20px;
    line-height: 1.2;
`;

const LoaderProgressPercent = styled.span`
    font-size: clamp(40px, 10vw, 64px);
    font-weight: 700;
    color: var(--poki-blue);
`;

const LoaderProgressBar = styled.div`
    display: flex;
    width: 100%;
    height: 20px;
    overflow: hidden;
    border: none;
    border-radius: 16px;
    background: var(--surface-muted);
    box-shadow: inset 0 2px 5px rgb(0 0 0 / 28%);
`;

const LoaderProgressFill = styled.div`
    width: ${({ $fillPercent }) => `${$fillPercent}%`};
    height: 100%;
    background: var(--poki-blue);
    transition: width .2s ease;
`;

const LoaderProgressDetails = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px;
    font-size: 16px;
    font-weight: 700;
    color: var(--text-secondary);
`;

function getFileCount(value) {
    if (!Number.isFinite(value) || value <= 0) {
        return 0;
    }

    return Math.floor(value);
}

function getByteCount(value) {
    if (!Number.isFinite(value) || value <= 0) {
        return 0;
    }

    return Math.floor(value);
}

function getProgressMessage(message) {
    return {
        loadedBytes: message.loadedBytes,
        totalBytes: message.totalBytes,
        progress: message.progress,
        filesStarted: message.filesStarted,
        filesFinished: message.filesFinished,
        filesPending: message.filesPending,
        approximate: message.approximate === true,
    };
}

function getDisplayProgressMessage(progressMessage, minimumTotalBytes) {
    const minimumByteCount = getByteCount(minimumTotalBytes);

    if (minimumByteCount <= 0) {
        return progressMessage;
    }

    return {
        ...progressMessage,
        totalBytes: Math.max(
            getByteCount(progressMessage.totalBytes),
            getByteCount(progressMessage.loadedBytes),
            minimumByteCount,
        ),
    };
}

function isLoadingFinishedMessage(message) {
    return message?.type === 'pokiMessageEvent'
        && message?.content?.event === SDKGameLoadingFinished;
}

export default function LoaderProgressOverlay({ hidden, iframeRef, iframeUrl, minimumTotalBytes }) {
    const dispatch = useDispatch();

    const [isVisible, setIsVisible] = useState(true);
    const [progressMessage, setProgressMessage] = useState(INITIAL_PROGRESS);
    const [progressPercent, setProgressPercent] = useState(0);
    const [filesTotal, setFilesTotal] = useState(0);

    const hideTimeoutRef = useRef(null);
    const sdkFinishedRef = useRef(false);
    const progressStopSentRef = useRef(false);

    const expectedOrigin = useMemo(() => getExpectedOrigin(iframeUrl), [iframeUrl]);

    useEffect(() => {
        const stopProgressMessages = () => {
            if (progressStopSentRef.current) {
                return;
            }

            const iframeWindow = iframeRef.current?.contentWindow;

            iframeWindow?.postMessage({ type: 'pokiProgressStop' }, expectedOrigin || '*');
            progressStopSentRef.current = true;
        };

        const hideAfterDelay = (delayMs) => {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = setTimeout(() => {
                stopProgressMessages();
                setIsVisible(false);
            }, delayMs);
        };

        const handleMessage = (event) => {
            const iframeWindow = iframeRef.current?.contentWindow;

            if (!iframeWindow || event.source !== iframeWindow) {
                return;
            }

            if (expectedOrigin && event.origin !== expectedOrigin) {
                return;
            }

            const message = event.data;

            if (!message || typeof message !== 'object') {
                return;
            }

            // SDK loading-finished is authoritative; filesPending only drives the fallback grace timer.
            if (isLoadingFinishedMessage(message)) {
                sdkFinishedRef.current = true;
                setProgressPercent(100);
                hideAfterDelay(SDK_FINISHED_HIDE_DELAY_MS);
                return;
            }

            if (message.type !== 'pokiProgress') {
                return;
            }

            const nextProgressMessage = getProgressMessage(message);
            const displayProgressMessage = getDisplayProgressMessage(nextProgressMessage, minimumTotalBytes);

            setProgressMessage(nextProgressMessage);
            dispatch(setLoaderProgress(nextProgressMessage));

            // Files are discovered progressively, so keep the total from moving backwards.
            setFilesTotal((prev) => Math.max(prev, getFileCount(nextProgressMessage.filesStarted)));

            if (sdkFinishedRef.current) {
                return;
            }

            if (nextProgressMessage.filesPending === 0) {
                hideAfterDelay(FILES_FINISHED_HIDE_DELAY_MS);
            } else {
                // Only advance while files are genuinely pending. A lull where every known file is
                // done isn't "finished" (a new batch usually follows), so we hold instead of jumping
                // to 100%. The bar completes only when the SDK reports loading finished.
                setProgressPercent((prev) => Math.max(prev, getLoaderAggregateProgressPercent(displayProgressMessage)));
                clearTimeout(hideTimeoutRef.current);
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
            clearTimeout(hideTimeoutRef.current);
        };
    }, [dispatch, expectedOrigin, iframeRef, minimumTotalBytes]);

    if (hidden || !isVisible) {
        return null;
    }

    const filesFinished = getFileCount(progressMessage.filesFinished);
    const displayProgressMessage = getDisplayProgressMessage(progressMessage, minimumTotalBytes);
    const estimateLabel = displayProgressMessage.approximate ? ' estimated' : '';
    const bytesLabel = `${formatBytes(displayProgressMessage.loadedBytes)} / ${formatBytes(displayProgressMessage.totalBytes)}${estimateLabel}`;
    const filesLabel = `${filesFinished}/${filesTotal} files`;

    return (
        <LoaderProgressOverlayWrapper>
            <LoaderProgressContent>
                <LoaderProgressLogo alt="Poki" />
                <LoaderProgressHeader>
                    <LoaderProgressTitle>Loading game</LoaderProgressTitle>
                    <LoaderProgressPercent>
                        {Math.round(progressPercent)}
                        %
                    </LoaderProgressPercent>
                </LoaderProgressHeader>
                <LoaderProgressBar
                    aria-valuemax="100"
                    aria-valuemin="0"
                    aria-valuenow={Math.round(progressPercent)}
                    role="progressbar"
                >
                    <LoaderProgressFill $fillPercent={progressPercent} />
                </LoaderProgressBar>
                <LoaderProgressDetails>
                    <span>{bytesLabel}</span>
                    <span>{filesLabel}</span>
                </LoaderProgressDetails>
            </LoaderProgressContent>
        </LoaderProgressOverlayWrapper>
    );
}

LoaderProgressOverlay.propTypes = {
    hidden: PropTypes.bool,
    iframeRef: PropTypes.shape({
        current: PropTypes.shape({
            contentWindow: PropTypes.object,
        }),
    }).isRequired,
    iframeUrl: PropTypes.string.isRequired,
    minimumTotalBytes: PropTypes.number,
};

LoaderProgressOverlay.defaultProps = {
    hidden: false,
    minimumTotalBytes: 0,
};
